const axios = require("axios");
const {observable} = require("mobx");
const {reportAndThrow} = require("helpers/errors");
const assign = require("object-assign");
const {createPipeline} = require("cleopatra");
const {createPromiseThrottle} = require("helpers/throttle");
const {pluckKeysExcept, decodeObservable} = require("helpers/object");
const {toQueryString} = require("helpers/url");


class ApiStore {

	@observable collection = [];
	@observable constraints = {};
	@observable metadata = {};

	constructor(collection = [], constraints = {}, metadata = {}, options = {}) {
		this.collection = collection;
		this.constraints = constraints;
		this.metadata = metadata;

		this.options = assign({
			autoFetch: true,
			changeDelay: 500,

		}, options);

		// Throttle constraint change
		const throttle = createPromiseThrottle(this.options.changeDelay);

		// Update constraints on capture
		this.constraintChange.capture(constraints => {
			this.constraints = constraints;
			return throttle().then(() => this.options.autoFetch ? this.fetch() : this.collection);
		});

		// Update collection on collection change
		this.collectionChange.capture(collection => this.collection = collection);

	}

	/////////////////////////////////
	// Configuration & Hooks
	/////////////////////////////////

	constraintChange = createPipeline().report(reportAndThrow);
	collectionChange = createPipeline().report(reportAndThrow);

	get endpoint() {
		return this.options.endpoint;
	}

	/////////////////////////////////
	// API Methods
	/////////////////////////////////

	constrain(constraints) {
		if (typeof  constraints === "function") {
			constraints = constraints(decodeObservable(this.constraints));
		}
		else {
			constraints = assign({}, decodeObservable(this.constraints), constraints);
		}

		return this.constraintChange.dispatch(constraints);
	}

	fetch() {
		return axios.get(`${this.endpoint}?${toQueryString(decodeObservable(this.constraints))}`)
			.then(response => {
				return this.collectionChange.dispatch(response.data.collection).then(collection => {
					// update metadata
					this.metadata = pluckKeysExcept(response.data, ["collection"]);
					return collection;
				});
			})
			.catch(reportAndThrow);
	}

	append(constraints, mutateElement = null) {
		// merge constraints
		constraints = assign({}, decodeObservable(this.constraints), constraints);

		return axios.get(`${this.endpoint}?${toQueryString(constraints)}`)
			.then(response => {
				const collection = decodeObservable(this.collection);

				// Merge collections
				response.data.collection.forEach(element => {
					const index = collection.findIndex(el => el.id === element.id);

					// remove old entry
					if (index > -1) {
						collection.splice(index, 1);
					}

					if (mutateElement && typeof mutateElement === 'function') {
						mutateElement(element);
					}

					collection.push(element);
				});

				return this.collectionChange.dispatch(collection).then(collection => {
					// update metadata
					this.metadata = pluckKeysExcept(response.data, ["collection"]);
					return collection;
				});
			});
	}

	create(data, options = {}) {
		return axios.post(this.endpoint, data, options)
			.then(response => {
				return this.add(response.data);
			})
			.catch(reportAndThrow);
	}

	update(id, data, options = {}) {
		return axios.post(`${this.endpoint}/${id}`, data, options)
			.then(response => {
				return this.replace([response.data]).then(collection => collection[0]);
			})
			.catch(reportAndThrow);
	}

	delete(id, options = {}) {
		return axios.delete(`${this.endpoint}/${id}`, null, options)
			.then(() => {
				const collection = Array.from(this.collection);
				const index = collection.findIndex(model => model.id === id);

				if (index > -1) {
					collection.splice(index, 1);
				}

				return this.collectionChange.dispatch(collection);
			})
			.catch(reportAndThrow);
	}

	/////////////////////////////////
	// Collection methods
	/////////////////////////////////

	add(element) {
		const collection = decodeObservable(this.collection);
		let index = collection.findIndex(el => el.id === element.id);

		if (index === -1) {
			index = collection.push(element) - 1;
		}

		return this.collectionChange.dispatch(collection)
			.then(collection => collection.filter(item => item.id === element.id) [0])
			.catch(reportAndThrow);
	}

	replace(elements) {
		if (!(elements instanceof Array)) {
			elements = [elements];
		}

		const collection = decodeObservable(this.collection);
		const ids = elements.map(element => element.id);

		elements.forEach(element => {
			const index = collection.findIndex(el => el.id === element.id);

			if (index === -1) {
				return;
			}

			collection[index] = element;
		});

		return this.collectionChange.dispatch(collection)
			.then(collection => collection.filter(element => ids.indexOf(element.id) !== -1))
			.catch(reportAndThrow);
	}

}

module.exports = ApiStore;



// WEBPACK FOOTER //
// ./resources/js/stores/abstract/ApiStore.js